/**
 * FormLookupField — the "normal lookup with gear" control (Binoy, Task 35: "one normal
 * lookup with gear. the other one a two mode lookup").
 *
 * Always shows the search/select UI (never hides itself based on whether a value is
 * selected) plus an optional gear/settings button. This is the SAME pairing App Studio's
 * Form Widget config already builds by hand (a bare FormLookupField + a separate FiSettings
 * IconButton opening LazyFormEditor, see WidgetEditorFields.tsx) — internalized here as one
 * `onEdit` prop instead of every consumer wiring the pairing themselves. Existing callers
 * that don't pass `onEdit` keep the old plain-lookup behavior unchanged.
 *
 * For the state-driven "shows lookup only when empty, shows resolved content + Clear/Switch
 * when set" behavior, use FormLookupFieldTwoMode instead — deliberately a separate component
 * (Binoy's explicit decision), not a mode flag on this one.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { FormDefinition } from '@atlas-forms/types-js';
import { useLookupDropdown } from '../useLookupDropdown';
import { getFormID, formatFormLabel } from './formLookupHelpers';
import './FormLookupField.css';

export interface FormLookupFieldProps {
  value: number | undefined;
  onChange: (formID: number | undefined, form?: FormDefinition) => void;
  /** Optional `page` (1-based) — pass it through to a paged backend call so "Load more" works;
   *  callers that ignore it simply never see more than their own first page. */
  fetchForms: (search?: string, page?: number) => Promise<FormDefinition[]>;
  loadForm?: (formID: number) => Promise<FormDefinition | undefined>;
  /** Called when the view (↗) button is clicked. Defaults to window.open. */
  onOpen?: (formID: number) => void;
  /** Gear/settings button — shown only when provided. Matches App Studio's established
   *  "lookup + gear opens the form editor" pairing (see file-level doc comment). */
  onEdit?: (formID: number) => void;
  editTitle?: string;
  placeholder?: string;
  className?: string;
}

const GearIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 13a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V19a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H4a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H10a1.65 1.65 0 001-1.51V4a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V10a1.65 1.65 0 001.51 1H20a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FormLookupField: React.FC<FormLookupFieldProps> = ({
  value,
  onChange,
  fetchForms,
  loadForm,
  onOpen,
  onEdit,
  editTitle,
  placeholder = 'Search forms…',
  className,
}) => {
  const [label, setLabel] = useState<string>('');
  const labelCache = useRef(new Map<number, string>());

  const {
    open, query, results, loading, loadingMore, hasMore, loadMore,
    containerRef, inputRef, toggleDropdown, closeDropdown, handleQueryChange,
  } = useLookupDropdown(fetchForms);

  useEffect(() => {
    if (value == null) { setLabel(''); return; }
    const cached = labelCache.current.get(value);
    if (cached) { setLabel(cached); return; }
    setLabel(String(value));
    if (loadForm) {
      let cancelled = false;
      loadForm(value)
        .then((form) => {
          if (cancelled) return;
          const l = form ? formatFormLabel({ ...form, FormID: getFormID(form) ?? value } as FormDefinition) : String(value);
          labelCache.current.set(value, l);
          setLabel(l);
        })
        .catch(() => { if (!cancelled) labelCache.current.set(value, String(value)); });
      return () => { cancelled = true; };
    }
    return undefined;
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectForm = useCallback((form: FormDefinition) => {
    const fid = getFormID(form);
    if (fid == null) return;
    const l = formatFormLabel({ ...form, FormID: fid } as FormDefinition);
    labelCache.current.set(fid, l);
    setLabel(l);
    onChange(fid, form);
    closeDropdown();
  }, [onChange, closeDropdown]);

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLabel('');
    onChange(undefined);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value != null && onOpen) onOpen(value);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value != null && onEdit) onEdit(value);
  };

  const hasSelection = label !== '';

  return (
    <div className={`af-form-lookup-row${className ? ` ${className}` : ''}`}>
      <div className="af-form-lookup" ref={containerRef}>
        <div className="af-form-lookup__trigger" onClick={toggleDropdown}>
          {hasSelection
            ? <span className="af-form-lookup__selected">{label}</span>
            : <span className="af-form-lookup__placeholder">{placeholder}</span>
          }
          <span className="af-form-lookup__actions">
            {hasSelection && (
              <>
                {onOpen && (
                  <button type="button" className="af-form-lookup__open-btn" onClick={handleView} title="Open form in new window">↗</button>
                )}
                <button type="button" className="af-form-lookup__clear-btn" onClick={clearSelection} title="Clear">✕</button>
              </>
            )}
            <span className="af-form-lookup__chevron">{open ? '▲' : '▼'}</span>
          </span>
        </div>

        {open && (
          <div className="af-form-lookup__dropdown">
            <div className="af-form-lookup__search-row">
              <input
                ref={inputRef}
                type="text"
                className="af-form-lookup__search"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                placeholder="Type to filter…"
              />
            </div>
            <div className="af-form-lookup__results">
              {loading && <div className="af-form-lookup__state">Loading…</div>}
              {!loading && results.length === 0 && (
                <div className="af-form-lookup__state">No forms found</div>
              )}
              {!loading && results
                .filter((f) => getFormID(f) != null)
                .map((form) => {
                  const fid = getFormID(form)!;
                  return (
                    <div
                      key={fid}
                      className={`af-form-lookup__result${fid === value ? ' af-form-lookup__result--selected' : ''}`}
                      onClick={() => selectForm(form)}
                    >
                      <span className="af-form-lookup__result-id">{fid}</span>
                      <span className="af-form-lookup__result-name">{form.Name ?? (form as any).name}</span>
                      {(form.Title ?? (form as any).title) && (
                        <span className="af-form-lookup__result-title">{form.Title ?? (form as any).title}</span>
                      )}
                    </div>
                  );
                })}
              {!loading && results.length > 0 && hasMore && (
                <button
                  type="button"
                  className="af-form-lookup__load-more"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {onEdit && (
        <button
          type="button"
          className="af-form-lookup__gear-btn"
          disabled={value == null}
          onClick={handleEdit}
          title={editTitle ?? (value != null ? 'Configure form' : 'Select a form first')}
        >
          <GearIcon />
        </button>
      )}
    </div>
  );
};
